import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import { forwardRef, useContext, useEffect, useState, useMemo } from 'react';
import uniqueId from '../../utils/uniqueId';

import DataPlaceholder from './DataPlaceholder';
import { UserStateContext, FormContext } from '../Context';

import './LabeledInput.scss';

/**ADA Compliant Input component
 * @param {Object} props Incoming JSX attributes.
 * @param {boolean} props.autofocus A boolean value that controls whether or not the input is focused automatically when navigating to the view.
 * @param {string} props.className A string of HTML classes delimited by a space character to be concatenated with the default component class for specific component targeting in styles or functionality.
 * @param {boolean} props.disabled A boolean value that will override the doingAjax UserState for variable control over when the component is enabled.
 * @param {boolean} props.forceValidation A boolean value that controls whether or not the input is validated onChange.
 * @param {string} props.formKey A string value that should be a path to the form state value represented by this component delimited by a "." character.
 * @param {string} props.initialValue A string value that will be directly passed to the HTML value attribute of the created element on first render.
 * @param {JSX.Element} props.internalIcon Element to display inside the input node.
 * @param {string} props.labelText A string value to be used as the label text. Default value is blank.
 * @param {function} props.onChange A function to be executed during the created elements onChange event. Receives the new value from the event object.
 * @param {string} props.placeholder A string value that will be displayed within the input when the input is empty.
 * @param {boolean} props.showDataPlaceholder If true, will display the standard data placeholder element instead of the input element.
 * @param {string} props.type A string value that corresponds to the desired type of the input.
 * @param {object} props.validatedObject An object that must be created via the useValidatedInput Hook.
 * @param {object} props.validators An array of objects. Each object in the array is evaluated during the validation effect allowing for multiple constraints on a value.
 * @param {object} ref A React Reference created via useReference or a mapper function.
 * @returns {JSX.Element} ADA Compliant Input component
 * @see {@link https://bitbucket.org/pcgeducation/shipyard/src/master/shipyard-web/src/components/shared/LabeledInput.md LabeledInput Documentation} for further information
 */
const LabeledInput = forwardRef((props, ref) => {
    let {
        validatedObject = null,
        forceValidation,
        disabled,
        labelText,
        placeholder,
        type,
        autofocus,
        validators,
        initialValue,
        onChange,
        onBlur,
        autoComplete,
        showDataPlaceholder,
        formKey,
        required,
        formatter,
    } = props;
    const { doingAjax } = useContext(UserStateContext);
    if (!type) type = 'text';
    const { triggerValidation, validationFinished } = useContext(FormContext);
    const [myId] = useState(uniqueId());
    const [isDisabled, setIsDisabled] = useState(
        typeof doingAjax === 'boolean' ? doingAjax : typeof props?.disabled === 'boolean' ? props.disabled : false
    );
    const [interacted, setInteracted] = useState(false);
    const [invalidReason, setInvalidReason] = useState('');
    const [value, setValue] = useState(initialValue ? (typeof formatter === 'function' ? formatter(initialValue) : initialValue) : '');
    const [isValid, setIsValid] = useState(true);
    const [displayDataPlaceholder, setDisplayDataPlaceholder] = useState(
        typeof showDataPlaceHolder === 'boolean' ? showDataPlaceholder : false
    );
    const [hasValidatedObject] = useState(validatedObject !== null);
    const [autocomplete] = useState(!!autoComplete ? autoComplete : null);
    const [masked, setMasked] = useState(typeof props?.masked === 'boolean' ? props.masked : false);
    const [isRequired, setIsRequired] = useState(typeof required === 'boolean' ? required : false);

    const maskEye = useMemo(() => {
        return (
            <FontAwesomeIcon
                icon={masked ? faEyeSlash : faEye}
                onClick={() => setMasked((prev) => !prev)}
            />
        );
    }, [masked]);

    const inputJSX = useMemo(() => {
        const handleChange = (e) => {
            setInteracted(true);
            hasValidatedObject ? validatedObject.setValue(e.target.value) : setValue(e.target.value);

            if (typeof onChange === 'function') onChange(e.target.value);

            if (forceValidation) {
                let i = 0,
                    limit = hasValidatedObject ? validatedObject.validators.length : validators.length;
                for (i; i < limit; i++) {
                    const validator = hasValidatedObject ? validatedObject.validators[i] : validators[i];
                    if (!validator.rgx.test(e.target.value)) {
                        hasValidatedObject ? validatedObject.setIsValid(false) : setIsValid(false);
                        setInvalidReason(validator.failText);
                        return;
                    }
                }
            }

            hasValidatedObject ? validatedObject.setIsValid(true) : setIsValid(true);
            setInvalidReason('');
        };

        const handleBlur = (e) => {
            if (typeof onBlur === 'function') onBlur(e.target.value);

            if (typeof formatter === 'function') setValue((prev) => formatter(prev));
        };

        return (
            <input
                type={masked ? 'password' : type}
                id={'input-' + myId}
                ref={ref}
                disabled={isDisabled}
                placeholder={placeholder}
                value={hasValidatedObject ? validatedObject.value : value}
                onChange={handleChange}
                onBlur={handleBlur}
                autoFocus={autofocus}
                aria-invalid={hasValidatedObject ? !validatedObject.isValid : !isValid}
                aria-describedby={'input_err-' + myId}
                data-form-key={!!formKey ? formKey : null}
                data-type={type}
                autoComplete={autocomplete}
            />
        );
    }, [
        masked,
        myId,
        isDisabled,
        hasValidatedObject,
        value,
        autocomplete,
        autofocus,
        forceValidation,
        formKey,
        isValid,
        placeholder,
        ref,
        type,
        validatedObject,
        validators,
        onChange,
        onBlur,
        formatter,
    ]);

    const displayValidationError = () => {
        if (forceValidation) return hasValidatedObject ? !validatedObject.isValid : !isValid;
        if (!interacted) return false;
        return hasValidatedObject ? !validatedObject.isValid : !isValid;
    };

    useEffect(() => {
        setValue((prev) => {
            let _val;
            hasValidatedObject
                ? typeof formatter === 'function'
                    ? (_val = formatter(validatedObject.value))
                    : (_val = validatedObject.value)
                : typeof props?.value !== 'undefined'
                  ? typeof formatter === 'function'
                      ? (_val = formatter(props.value))
                      : (_val = props.value)
                  : (_val = prev);

            return _val;
        });

        if (forceValidation) {
            let i = 0,
                limit = hasValidatedObject ? validatedObject.validators.length : validators.length;
            for (i; i < limit; i++) {
                const validator = hasValidatedObject ? validatedObject.validators[i] : validators[i];
                if (!validator.rgx.test(hasValidatedObject ? validatedObject.value : props.value)) {
                    hasValidatedObject ? validatedObject.setIsValid(false) : setIsValid(false);
                    setInvalidReason(validator.failText);
                    return;
                }
            }

            hasValidatedObject ? validatedObject.setIsValid(true) : setIsValid(true);
            setInvalidReason('');
        }
    }, [props.value, validators, forceValidation, hasValidatedObject, validatedObject, formatter]);

    useEffect(() => {
        if (triggerValidation) {
            if (!hasValidatedObject && typeof validators?.length === 'number') {
                for (let i = 0; i < validators.length; i++) {
                    const validator = validators[i];
                    if (!validator.rgx.test(value)) {
                        setIsValid(false);
                        setInvalidReason(validator.failText);
                        validationFinished(true);
                        return;
                    }
                }
            }

            setIsValid(true);
            setInvalidReason('');
            validationFinished(true);
        }
    }, [value, hasValidatedObject, validators, triggerValidation, validationFinished, isValid]);

    useEffect(() => {
        setIsDisabled((prev) => (typeof disabled === 'boolean' ? disabled : typeof doingAjax === 'boolean' ? doingAjax : prev));
    }, [disabled, doingAjax]);

    useEffect(() => {
        setDisplayDataPlaceholder(showDataPlaceholder);
    }, [showDataPlaceholder]);

    useEffect(() => {
        setIsRequired((prev) => (typeof required === 'boolean' ? required : prev));
    }, [required]);

    return (
        <div className={(props.className ? props.className + ' ' : '') + 'labeled-input ' + (displayValidationError() ? 'invalid' : '')}>
            <label>
                {isRequired ? <span className="is-required">&#8270;</span> : null} {labelText}
                <br />
                {displayDataPlaceholder ? <DataPlaceholder /> : inputJSX}
                {typeof props.masked === 'boolean' && props.masked ? maskEye : null}
            </label>
            <span
                className="invalid-reason"
                id={'input_err-' + myId}
            >
                {invalidReason}
            </span>
        </div>
    );
});

LabeledInput.displayName = 'LabeledInput';

export default LabeledInput;
